import {
  TAuthConverter,
  TConvertor,
  TCreateConverter,
  TFetchConverter,
} from 'data/types/Converter.types';
import {
  TDocPacketUI,
  TDocPacket,
  THPVisitation,
  THPVisitationUI,
  THPContactInfoUI,
  THPContactInfo,
  TNameUpdateUI,
  TNameUpdate,
  TEmailUpdateUI,
  TEmailUpdate,
  TPhoneUpdateUI,
  TPhoneUpdate,
  TFaxUpdateUI,
  TFaxUpdate,
  THPVisitationUpdateUI,
  THPVisitationUpdate,
  EHPDocPacketTypes,
  EHPDocPacketTypesUI,
  EHPVisitation,
  EHPVisitationUI,
  TDocPacketGetUI,
  TDocPacketGet,
  THPLetterUpdateUI,
  THPLetterUpdate,
  THPFormUpdateUI,
  THPFormUpdate,
  EDocPacketStatusUi,
  EDocPacketStatus,
  TAnimalDetailAddDocPacketUI,
  TAnimalDetailAddDocPacket,
  TAnimalDetailHPVisitationUI,
  TAnimalDetailHPVisitation,
  TDocPacketInfoUI,
  TDocPacketInfoDB,
  TResidentMoveUi,
  TResidentMove,
  TResidenceMoveInfo,
  TResidenceMoveInfoUI,
  TPropertyUI,
  TPropertyDB,
} from 'data/types/DocPacket.types';
import { fileDataTypeConverter } from './General.converters';
import { formattedDate } from 'helpers/utils';
import moment from 'moment';

export const docPacketStatusConverter: TConvertor<
  EDocPacketStatusUi,
  EDocPacketStatus
> = {
  fromDb: data => {
    const statuses: {
      [key in EDocPacketStatus]: EDocPacketStatusUi;
    } = {
      [EDocPacketStatus.NONE]: EDocPacketStatusUi.NONE,
      [EDocPacketStatus.VERIFIED]: EDocPacketStatusUi.VERIFIED,
    };
    return statuses[data];
  },
  toDb: data => {
    const statuses: {
      [key in EDocPacketStatusUi]: EDocPacketStatus;
    } = {
      [EDocPacketStatusUi.NONE]: EDocPacketStatus.NONE,
      [EDocPacketStatusUi.VERIFIED]: EDocPacketStatus.VERIFIED,
    };
    return statuses[data];
  },
};

export const hpDocTypeConvertor: TConvertor<
  EHPDocPacketTypesUI,
  EHPDocPacketTypes
> = {
  fromDb: data => {
    return EHPDocPacketTypesUI[
      EHPDocPacketTypes[data] as keyof typeof EHPDocPacketTypesUI
    ];
  },
  toDb: data => {
    return EHPDocPacketTypes[
      EHPDocPacketTypesUI[data] as keyof typeof EHPDocPacketTypes
    ];
  },
};

export const hpVisitationTypeConvertor: TConvertor<
  EHPVisitationUI,
  EHPVisitation
> = {
  fromDb: data => {
    return EHPVisitationUI[EHPVisitation[data] as keyof typeof EHPVisitationUI];
  },
  toDb: data => {
    return EHPVisitation[EHPVisitationUI[data] as keyof typeof EHPVisitation];
  },
};

export const hpVisitationConvertor: TCreateConverter<
  THPVisitationUI,
  THPVisitation
> = {
  toDb: data => ({
    websiteAdvertised: data?.websiteAdvertised === 'yes',
    websites: data.websites?.filter(website => !!website),
  }),
};

export const hpContactInfoConvertor: TAuthConverter<
  THPContactInfoUI,
  THPContactInfo,
  THPContactInfoUI,
  THPContactInfo
> = {
  toDb: data => ({
    name: data.name,
    emailAddress: data.emailAddress?.trim() || undefined,
    phoneNumber: data.phoneNumber
      ? `${data.phoneNumber[0] === '+' ? '' : '+'}${data.phoneNumber}`
      : undefined,
    faxNumber: data.faxNumber?.trim() || undefined,
    patientName: data.patientName,
    patientDateOfBirth: data.patientDateOfBirth
      ? formattedDate(data.patientDateOfBirth, 'YYYY-MM-DD')
      : null,
  }),
  fromDb: data => ({
    name: data.name,
    emailAddress: data.emailAddress,
    phoneNumber: data.phoneNumber,
    faxNumber: data.faxNumber,
    patientName: data?.patientName || '',
    patientDateOfBirth: data?.patientDateOfBirth
      ? moment(data.patientDateOfBirth)
      : null,
  }),
};

export const hpNameUpdateConvertor: TCreateConverter<
  TNameUpdateUI,
  TNameUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    hpContactName: data.value || null,
  }),
};

export const hpEmailUpdateConvertor: TCreateConverter<
  TEmailUpdateUI,
  TEmailUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    hpContactEmail: data.value || null,
  }),
};

export const hpPhoneUpdateConvertor: TCreateConverter<
  TPhoneUpdateUI,
  TPhoneUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    hpContactPhone: data.value
      ? `${data.value[0] === '+' ? '' : '+'}${data.value}`
      : null,
  }),
};

export const hpFaxUpdateConvertor: TCreateConverter<TFaxUpdateUI, TFaxUpdate> =
  {
    toDb: data => ({
      documentPacketId: data.documentPacketId,
      hpContactFaxNumber: data.value || null,
    }),
  };

export const hpVisitationUpdateConvertor: TCreateConverter<
  THPVisitationUpdateUI,
  THPVisitationUpdate
> = {
  toDb: data => ({
    documentPacketId: data.documentPacketId,
    hpVisitation: hpVisitationConvertor.toDb(data.hpVisitation),
  }),
};

export const hpLetterUpdateConvertor: TCreateConverter<
  THPLetterUpdateUI,
  THPLetterUpdate
> = {
  toDb: data => ({
    documentPacketId: data.hpDocumentPacketId,
    newHpLetters: data.newHpLetters || [],
    removedHpLetterIds: data.removedDocumentsIds || [],
  }),
};
export const hpFormUpdateConvertor: TCreateConverter<
  THPFormUpdateUI,
  THPFormUpdate
> = {
  toDb: data => ({
    tenantId: data.tenantId,
    documentPacketId: data.hpDocumentPacketId,
    newHpForms: data.newHpForms || [],
    removedHpFormIds: data.removedHpFormIds || [],
  }),
};

export const getHPDocPacketsConvertor: TFetchConverter<
  TDocPacketGetUI,
  TDocPacketGet
> = {
  fromDb: data => {
    return {
      id: data.hpDocumentPacketId,
      isDeleted: data.isDeleted,
      status: docPacketStatusConverter.fromDb(data.verificationStatus),
      hpDocumentPacketId: data.hpDocumentPacketId,
      hpContactInfo: hpContactInfoConvertor.fromDb(data.hpContactInfo),
      websites: data.hpVisitation?.websites?.map(item => item?.value),
      hpVisitation:
        data.hpVisitation === null ||
        data.hpVisitation?.websiteAdvertised === null
          ? 'Not specified'
          : data.hpVisitation?.websiteAdvertised
          ? 'Yes'
          : 'No',
      hpDocForms: (data.hpDocForms || []).map(file =>
        fileDataTypeConverter.fromDb(file),
      ),
      hpDocLetters: (data.hpDocLetters || []).map(file =>
        fileDataTypeConverter.fromDb(file),
      ),
      verificationStatus: data.verificationStatus,
      canEdit: data.modifiable || false,
      receivedAfterCompletingQuestionnaireOrInterview:
        data.receivedAfterCompletingQuestionnaireOrInterview,
      roiSignature: data.roiSignature,
    };
  },
};

export const createHPDocConvertor: TCreateConverter<TDocPacketUI, TDocPacket> =
  {
    toDb: data => ({
      hpContactInfo: hpContactInfoConvertor.toDb({
        name: data.name,
        emailAddress: data.emailAddress,
        phoneNumber: data.phoneNumber,
        faxNumber: data.faxNumber,
        patientName: data.patientName || '',
      }),
      hpDocType: hpDocTypeConvertor.toDb(data.hpDocType),
      hpVisitation: hpVisitationConvertor.toDb({
        websiteAdvertised: data.hpVisitation,
        websites: data.websites?.filter(item => !!item),
      }),
      hpDocPacketLetter: data.hpDocPacketLetter ?? [],
    }),
  };

export const animalDetailhpVisitationConvertor: TCreateConverter<
  TAnimalDetailHPVisitationUI,
  TAnimalDetailHPVisitation
> = {
  toDb: data => {
    return {
      websiteAdvertised: data.websiteAdvertised === 'yes',
      websites: data.websites
        ? [data.websites]?.filter(website => !!website)
        : undefined,
    };
  },
};

export const animalDetailAddDocPacketConvertor: TCreateConverter<
  TAnimalDetailAddDocPacketUI,
  TAnimalDetailAddDocPacket
> = {
  toDb: data => ({
    hpContactInfo: hpContactInfoConvertor.toDb({
      name: data.name,
      emailAddress: data.emailAddress,
      phoneNumber: data.phoneNumber,
      faxNumber: data.faxNumber,
      ...(data.patientName && { patientName: data.patientName }),
      ...(data.patientDateOfBirth && {
        patientDateOfBirth: data.patientDateOfBirth,
      }),
    }),
    // hpDocType: data.hpDocType
    //   ? hpDocTypeConvertor.toDb(data.hpDocType)
    //   : undefined,
    hpVisitation: data.websiteAdvertised
      ? animalDetailhpVisitationConvertor.toDb({
          websiteAdvertised: data.websiteAdvertised,
          websites: data.websiteAdvertised,
        })
      : undefined,
    hpDocPacketLetter: data.hpDocPacketLetter ?? [],
    receivedAfterCompletingQuestionnaireOrInterview: null,
    tenantId: data.tenantId,
  }),
};

export const createHPDocInfoConvertor: TCreateConverter<
  TDocPacketInfoUI,
  TDocPacketInfoDB
> = {
  toDb: data => {
    const hpVisitation = data.websiteAdvertised
      ? {
          websiteAdvertised: data.websiteAdvertised === 'yes',
          websites:
            typeof data.websites === 'object'
              ? [...data.websites]
              : [data.websites],
        }
      : null;
    const receivedAfterCompletingQuestionnaireOrInterview =
      data.receivedAfterCompletingQuestionnaireOrInterview
        ? data.receivedAfterCompletingQuestionnaireOrInterview === 'yes'
        : null;

    return {
      hpDocumentPacketId: data.hpDocumentPacketId,
      receivedAfterCompletingQuestionnaireOrInterview,
      hpVisitation,
      tenantId: data.tenantId,
      roiSignature: data.roiSignature,
    };
  },
};

export const ResidentMoveConverter: TCreateConverter<
  TResidentMoveUi,
  TResidentMove
> = {
  toDb: data => {
    return {
      residenceId: data.residenceId,
      propertyId: data.property.value,
      unitNumber: data.unit.label,
      ...(data.endTime && {
        leaseTo: formattedDate(data.endTime, 'YYYY-MM-DD'),
      }),
      ...(data.startTime && {
        moveIn: formattedDate(data.startTime, 'YYYY-MM-DD'),
      }),
    };
  },
};

export const residenceMoveConverter: TFetchConverter<
  TResidenceMoveInfoUI,
  TResidenceMoveInfo
> = {
  fromDb: data => {
    return {
      isPolicyChanging: data.isPolicyChanging,
    };
  },
};

export const propertiesGetConverter: TFetchConverter<TPropertyUI, TPropertyDB> =
  {
    fromDb: data => {
      return {
        name: data.name,
        value: data.id,
      };
    },
  };
